import type {
  ResponseContractError,
  ResponseRequestContext,
} from '../contracts/response-contract-error'
import type { ResponseUser } from '../types'

type RollbarReporter = Readonly<{
  error(error: Error, metadata: unknown): unknown
}>

type DiagnosticLogger = Readonly<{
  error(message: string, error: unknown): unknown
}>

const sensitiveKey = /password|passcode|token|cookie|authorization|secret/i

function scrubUrl(value: string | undefined): string | undefined {
  if (!value) return value
  try {
    const absolute = /^[a-z][a-z\d+.-]*:/i.test(value)
    const url = new URL(value, 'https://response-contract.invalid')
    for (const key of [...url.searchParams.keys()]) {
      if (sensitiveKey.test(key)) url.searchParams.set(key, '[scrubbed]')
    }
    return absolute ? url.toString() : `${url.pathname}${url.search}${url.hash}`
  } catch {
    return '[unparseable-url]'
  }
}

function scrub(value: unknown, key = ''): unknown {
  if (sensitiveKey.test(key)) return '[scrubbed]'
  if (Array.isArray(value)) return value.map((item) => scrub(item))
  if (value && typeof value === 'object') {
    return Object.fromEntries(
      Object.entries(value).map(([childKey, childValue]) => [childKey, scrub(childValue, childKey)]),
    )
  }
  return value
}

export function buildResponseContractReport(
  error: ResponseContractError,
  context: ResponseRequestContext,
  user: ResponseUser | null,
) {
  const issuePaths = [...new Set(error.issues.map((issue) => issue.path))].sort()

  return {
    fingerprint: `response-contract:${error.contract}:${issuePaths.join(',')}`,
    person: user,
    request: {
      id: context.id,
      semantics: context.semantics,
      action: context.action,
      component: context.component,
      operation: context.operation,
      url: scrubUrl(context.url),
      status: context.status,
    },
    contract: error.contract,
    issues: error.issues,
    safeValues: scrub(error.safeValues) as Record<string, unknown>,
  }
}

export function reportResponseContractError(
  reporter: RollbarReporter | undefined,
  error: ResponseContractError,
  context: ResponseRequestContext,
  user: ResponseUser | null,
  logger?: DiagnosticLogger,
): void {
  if (!reporter) return

  try {
    reporter.error(error, buildResponseContractReport(error, context, user))
  } catch (reportingError) {
    logger?.error('Failed to report response contract error', reportingError)
  }
}
